/* ===================== Direct S3 Upload  ===================== */
import { uploadToS3 } from "../utils/uploadToS3";
import api from "./axios";

export const feedApi = {
  /* ===================== FETCH FEED (Cursor-based pagination) ===================== */

  getFeed: async ({
    type = "PUBLIC", // PUBLIC | PRIME
    cursor = null, // Use cursor instead of page
    limit = 10,
    signal,
  }) => {
    const params = { limit };

    // If cursor is provided, use it; otherwise, it's the first page
    if (cursor) {
      params.cursor = cursor;
    }

    const res = await api.get(`/feeds/${type.toLowerCase()}`, {
      params,
      signal,
    });

    return res.data;
  },

  /* ===================== CREATE POST ===================== */
  createPost: async ({
    description,
    feedType,
    images = [],
    video = null,
    isMonetized = false,
    onUploadProgress,
  }) => {
    const hasVideo = !!video?.file;
    const totalUnits = images.length + (hasVideo ? 1 : 0);

    // Per-unit progress (0-100 each), combined into one overall percentage.
    // unitProgress[i] = images[0..n-1], then video as the last slot if present.
    const unitProgress = new Array(totalUnits).fill(0);

    const reportOverall = () => {
      if (totalUnits === 0) return;
      const sum = unitProgress.reduce((a, b) => a + b, 0);
      const overall = Math.round(sum / totalUnits);
      onUploadProgress?.({ loaded: overall, total: 100 });
    };

    //
    // IMAGE UPLOADS (run in parallel, each tracked independently)
    //
    const imageUploadPromises = images.map((img, i) =>
      uploadToS3({
        file: img.file,
        mediaType: "image",
        onProgress: (percent) => {
          unitProgress[i] = percent;
          reportOverall();
        },
      }),
    );

    //
    // VIDEO UPLOAD (runs alongside images, not after them)
    //
    const videoUnitIndex = images.length; // last slot in unitProgress
    const videoUploadPromise = hasVideo
      ? uploadToS3({
          file: video.file,
          mediaType: "video",
          onProgress: (percent) => {
            unitProgress[videoUnitIndex] = percent;
            reportOverall();
          },
        })
      : Promise.resolve(null);

    const [imageKeys, videoKey] = await Promise.all([
      Promise.all(imageUploadPromises),
      videoUploadPromise,
    ]);

    //
    // CREATE POST
    //
    const res = await api.post("/feeds/create", {
      description,
      feedType,
      isMonetized,
      images: imageKeys,
      video: videoKey,
    });

    return res.data;
  },

  /* ===================== UPDATE POST ===================== */
  updatePost: async ({
    postId,
    description,
    images = [],
    video = null,
    onUploadProgress,
  }) => {
    const newImages = images.filter((img) => img.file);
    const hasVideo = !!video?.file;
    const totalUnits = newImages.length + (hasVideo ? 1 : 0);

    const unitProgress = new Array(totalUnits).fill(0);

    const reportOverall = () => {
      if (totalUnits === 0) return;
      const sum = unitProgress.reduce((a, b) => a + b, 0);
      const overall = Math.round(sum / totalUnits);
      onUploadProgress?.({ loaded: overall, total: 100 });
    };

    //
    // NEW IMAGES (parallel)
    //
    const imageUploadPromises = newImages.map((image, i) =>
      uploadToS3({
        file: image.file,
        mediaType: "image",
        onProgress: (percent) => {
          unitProgress[i] = percent;
          reportOverall();
        },
      }),
    );

    //
    // NEW VIDEO (alongside images)
    //
    const videoUnitIndex = newImages.length;
    const videoUploadPromise = hasVideo
      ? uploadToS3({
          file: video.file,
          mediaType: "video",
          onProgress: (percent) => {
            unitProgress[videoUnitIndex] = percent;
            reportOverall();
          },
        })
      : Promise.resolve(null);

    const [imageKeys, videoKey] = await Promise.all([
      Promise.all(imageUploadPromises),
      videoUploadPromise,
    ]);

    const res = await api.patch(`/feeds/${postId}`, {
      description,
      images: imageKeys,
      video: videoKey,
    });

    return res.data;
  },

  /* ===================== DELETE POST ===================== */
  deletePost: async ({ postId }) => {
    const res = await api.delete(`/feeds/${postId}`);
    return res.data;
  },

  /* ===================== LIKE / SAVE ===================== */
  toggleLike: async ({ postId }) => {
    const res = await api.post(`/likes/${postId}`);
    return res.data;
  },

  /* ===================== POST BOOKMARK ===================== */
  toggleSave: async ({ postId }) => {
    const res = await api.post(`/saves/${postId}`);
    return res.data;
  },

  /* ===================== FOLLOW ===================== */
  followUser: async ({ userId }) => {
    const res = await api.post(`/follows/${userId}/follow`);
    return res.data;
  },

  handleRemoveProfile: async ({ type }) => {
    await api.patch(`user/removeProfile`, {
      type: type,
    });
  },

  /* ===================== get state post (Cursor-based) ===================== */

  getStatePost: async ({ cursor = null, limit = 10 }) => {
    const params = { tab: "STATE", limit };
    if (cursor) {
      params.cursor = cursor;
    }
    const res = await api.get(`/feeds/prime`, { params });
    return res.data;
  },

  /* ===================== get district post (Cursor-based) ===================== */

  getDistrictPost: async ({ cursor = null, limit = 10 }) => {
    const params = { tab: "DISTRICT", limit };
    if (cursor) {
      params.cursor = cursor;
    }
    const res = await api.get(`/feeds/prime`, { params });
    return res.data;
  },

  /* ===================== get national post (Cursor-based) ===================== */

  getNationalPost: async ({ cursor = null, limit = 10 }) => {
    const params = { tab: "NATIONAL", limit };
    if (cursor) {
      params.cursor = cursor;
    }
    const res = await api.get(`/feeds/prime`, { params });
    return res.data;
  },

  /* ===================== get All post ===================== */

  getAllPost: async () => {
    const res = await api.get(`/feeds/prime?tab=ALL`);
    return res.data;
  },

  /* ===================== UNFOLLOW ===================== */

  unfollowUser: async ({ userId }) => {
    const res = await api.delete(`/follows/${userId}`);
    return res.data;
  },

  /* ===================== FOLLOW REQUEST ===================== */
  requestFollow: async ({ userId }) => {
    const res = await api.post(`/follows/${userId}/follow`);
    return res.data;
  },

  /* ===================== FOLLOW REQUEST CANCEL ===================== */
  requestCancel: async ({ userId }) => {
    const res = await api.delete(`/follows/request/${userId}/cancel`);
    return res.data;
  },

  // /* ===================== GET ALL COMMENTS OF POST ===================== */
  // getCommentsByPost: async ({ postId }) => {
  //   const res = await api.get(`/comments/${postId}/comments`);
  //   return res.data; // { comments }
  // },

  /* ===================== GET ALL COMMENTS OF POST (with pagination) ===================== */
  getCommentsByPost: async ({ postId, cursor = null, limit = 10 }) => {
    const params = { limit };
    if (cursor) {
      params.cursor = cursor;
    }
    const res = await api.get(`/comments/${postId}/comments`, { params });
    return res.data; // { comments, count, hasMore, nextCursor }
  },

  /* ===================== ADD COMMENTS ===================== */
  addComment: async ({ postId, content }) => {
    const res = await api.post(`/comments/${postId}/comment`, { content });

    return res.data; // { success, comment }
  },

  /* ===================== GET REPLIES OF A COMMENT ===================== */
  getRepliesByComment: async ({ commentId }) => {
    const res = await api.get(`/comments/comment/${commentId}/replies`);
    return res.data; // { replies }
  },

  /* ===================== COMMENT REPLY ===================== */
  addCommentReply: async ({ postId, content, parentCommentId }) => {
    const res = await api.post(`/comments/${postId}/comment`, {
      content,
      parentCommentId, // optional (used for replies)
    });
    return res.data; // { success, comment }
  },

  /* ===================== COMMENT LIKE ===================== */
  likeComment: async ({ commentId }) => {
    const res = await api.post(`/comments/comment/${commentId}/like`);
    return res.data;
  },

  /* ===================== REPORT POST ===================== */
  reportPost: async ({ postId, reason }) => {
    if (!postId || !reason) {
      throw new Error("postId and reason are required");
    }

    const res = await api.post(`/feeds/${postId}/report`, { reason });

    return res.data;
    // { success, message }
  },

  /* ===================== GET SINGLE POST (Shared Post) ===================== */
  getPostById: async ({ postId }) => {
    if (!postId) {
      throw new Error("postId is required");
    }

    const res = await api.get(`/feeds/${postId}`);

    return res.data;
  },

  getMonetizedPostLimits: async ({ signal } = {}) => {
    const res = await api.get(`/user/monetized-post-limits`, { signal });
    return res.data; // { monetizedPostLimit, monetizedPostCount }
  },

  /* ===================== GET MONETIZED POST COUNT FOR DAY  ===================== */
  monetizedPostsCount: async () => {
    const res = await api.get(`/feeds`);

    return res.data;
  },
};

// /* ===================== Direct S3 Upload  ===================== */
// import { uploadToS3 } from "../utils/uploadToS3";
// import api from "./axios";

// export const feedApi = {
//   /* ===================== FETCH FEED (Cursor-based pagination) ===================== */

//   getFeed: async ({
//     type = "PUBLIC", // PUBLIC | PRIME
//     cursor = null, // Use cursor instead of page
//     limit = 10,
//     signal,
//   }) => {
//     const params = { limit };

//     // If cursor is provided, use it; otherwise, it's the first page
//     if (cursor) {
//       params.cursor = cursor;
//     }

//     const res = await api.get(`/feeds/${type.toLowerCase()}`, {
//       params,
//       signal,
//     });

//     return res.data;
//   },

//   /* ===================== CREATE POST ===================== */
//   createPost: async ({
//     description,
//     feedType,
//     images = [],
//     video = null,
//     isMonetized = false,
//     onUploadProgress,
//   }) => {
//     let imageKeys = [];
//     let videoKey = null;

//     //
//     // IMAGE UPLOADS
//     //
//     if (images.length > 0) {
//       for (let i = 0; i < images.length; i++) {
//         const key = await uploadToS3({
//           file: images[i].file,
//           mediaType: "image",

//           onProgress: (percent) => {
//             const overall = Math.round(
//               ((i + percent / 100) / images.length) * 100,
//             );

//             onUploadProgress?.({
//               loaded: overall,
//               total: 100,
//             });
//           },
//         });

//         imageKeys.push(key);
//       }
//     }

//     //
//     // VIDEO UPLOAD
//     //
//     if (video?.file) {
//       videoKey = await uploadToS3({
//         file: video.file,
//         mediaType: "video",

//         onProgress: (percent) => {
//           onUploadProgress?.({
//             loaded: percent,
//             total: 100,
//           });
//         },
//       });
//     }

//     //
//     // CREATE POST
//     //
//     const res = await api.post("/feeds/create", {
//       description,
//       feedType,
//       isMonetized,

//       images: imageKeys,
//       video: videoKey,
//     });

//     return res.data;
//   },

//   /* ===================== UPDATE POST ===================== */
//   updatePost: async ({ postId, description, images = [], video = null }) => {
//     let imageKeys = [];
//     let videoKey = null;

//     //
//     // NEW IMAGES
//     //
//     const newImages = images.filter((img) => img.file);

//     for (const image of newImages) {
//       const key = await uploadToS3({
//         file: image.file,
//         mediaType: "image",
//       });

//       imageKeys.push(key);
//     }

//     //
//     // NEW VIDEO
//     //
//     if (video?.file) {
//       videoKey = await uploadToS3({
//         file: video.file,
//         mediaType: "video",
//       });
//     }

//     const res = await api.patch(`/feeds/${postId}`, {
//       description,
//       images: imageKeys,
//       video: videoKey,
//     });

//     return res.data;
//   },

//   /* ===================== DELETE POST ===================== */
//   deletePost: async ({ postId }) => {
//     const res = await api.delete(`/feeds/${postId}`);
//     return res.data;
//   },

//   /* ===================== LIKE / SAVE ===================== */
//   toggleLike: async ({ postId }) => {
//     const res = await api.post(`/likes/${postId}`);
//     return res.data;
//   },

//   /* ===================== POST BOOKMARK ===================== */
//   toggleSave: async ({ postId }) => {
//     const res = await api.post(`/saves/${postId}`);
//     return res.data;
//   },

//   /* ===================== FOLLOW ===================== */
//   followUser: async ({ userId }) => {
//     const res = await api.post(`/follows/${userId}/follow`);
//     return res.data;
//   },

//   handleRemoveProfile: async ({ type }) => {
//     await api.patch(`user/removeProfile`, {
//       type: type,
//     });
//   },

//   /* ===================== get state post (Cursor-based) ===================== */

//   getStatePost: async ({ cursor = null, limit = 10 }) => {
//     const params = { tab: "STATE", limit };
//     if (cursor) {
//       params.cursor = cursor;
//     }
//     const res = await api.get(`/feeds/prime`, { params });
//     return res.data;
//   },

//   /* ===================== get district post (Cursor-based) ===================== */

//   getDistrictPost: async ({ cursor = null, limit = 10 }) => {
//     const params = { tab: "DISTRICT", limit };
//     if (cursor) {
//       params.cursor = cursor;
//     }
//     const res = await api.get(`/feeds/prime`, { params });
//     return res.data;
//   },

//   /* ===================== get national post (Cursor-based) ===================== */

//   getNationalPost: async ({ cursor = null, limit = 10 }) => {
//     const params = { tab: "NATIONAL", limit };
//     if (cursor) {
//       params.cursor = cursor;
//     }
//     const res = await api.get(`/feeds/prime`, { params });
//     return res.data;
//   },

//   /* ===================== get All post ===================== */

//   getAllPost: async () => {
//     const res = await api.get(`/feeds/prime?tab=ALL`);
//     return res.data;
//   },

//   /* ===================== UNFOLLOW ===================== */

//   unfollowUser: async ({ userId }) => {
//     const res = await api.delete(`/follows/${userId}`);
//     return res.data;
//   },

//   /* ===================== FOLLOW REQUEST ===================== */
//   requestFollow: async ({ userId }) => {
//     const res = await api.post(`/follows/${userId}/follow`);
//     return res.data;
//   },

//   /* ===================== FOLLOW REQUEST CANCEL ===================== */
//   requestCancel: async ({ userId }) => {
//     const res = await api.delete(`/follows/request/${userId}/cancel`);
//     return res.data;
//   },

//   /* ===================== GET ALL COMMENTS OF POST ===================== */
//   getCommentsByPost: async ({ postId }) => {
//     const res = await api.get(`/comments/${postId}/comments`);
//     return res.data; // { comments }
//   },

//   /* ===================== ADD COMMENTS ===================== */
//   addComment: async ({ postId, content }) => {
//     const res = await api.post(`/comments/${postId}/comment`, { content });

//     return res.data; // { success, comment }
//   },

//   /* ===================== GET REPLIES OF A COMMENT ===================== */
//   getRepliesByComment: async ({ commentId }) => {
//     const res = await api.get(`/comments/comment/${commentId}/replies`);
//     return res.data; // { replies }
//   },

//   /* ===================== COMMENT REPLY ===================== */
//   addCommentReply: async ({ postId, content, parentCommentId }) => {
//     const res = await api.post(`/comments/${postId}/comment`, {
//       content,
//       parentCommentId, // optional (used for replies)
//     });
//     return res.data; // { success, comment }
//   },

//   /* ===================== COMMENT LIKE ===================== */
//   likeComment: async ({ commentId }) => {
//     const res = await api.post(`/comments/comment/${commentId}/like`);
//     return res.data;
//   },

//   /* ===================== REPORT POST ===================== */
//   reportPost: async ({ postId, reason }) => {
//     if (!postId || !reason) {
//       throw new Error("postId and reason are required");
//     }

//     const res = await api.post(`/feeds/${postId}/report`, { reason });

//     return res.data;
//     // { success, message }
//   },

//   /* ===================== GET SINGLE POST (Shared Post) ===================== */
//   getPostById: async ({ postId }) => {
//     if (!postId) {
//       throw new Error("postId is required");
//     }

//     const res = await api.get(`/feeds/${postId}`);

//     return res.data;
//   },

//   /* ===================== MONETIZED POST LIMIT  ===================== */
//   getMonetizedPostLimits: async ({ signal } = {}) => {
//     const res = await api.get(`/user/monetized-post-limits`, { signal });

//     return res.data;
//   },

//   /* ===================== GET MONETIZED POST COUNT FOR DAY  ===================== */
//   monetizedPostsCount: async () => {
//     const res = await api.get(`/feeds`);

//     return res.data;
//   },
// };

// =====================================================================================================================

// import api from "./axios";

// export const feedApi = {
//  /* ===================== FETCH FEED (Cursor-based pagination) ===================== */

//  getFeed: async ({
//    type = "PUBLIC", // PUBLIC | PRIME
//    cursor = null, // Use cursor instead of page
//    limit = 10,
//    signal,
//  }) => {
//    const params = { limit };

//    // If cursor is provided, use it; otherwise, it's the first page
//    if (cursor) {
//      params.cursor = cursor;
//    }

//    const res = await api.get(`/feeds/${type.toLowerCase()}`, {
//      params,
//      signal,
//    });

//    return res.data;
//  },

//  /* ===================== CREATE POST ===================== */
//  createPost: async ({
//    description,
//    feedType, // PUBLIC | PRIME
//    images = [],
//    video,
//    onUploadProgress,
//    isMonetized = false,
//  }) => {
//    const formData = new FormData();

//    formData.append("description", description);
//    formData.append("feedType", feedType);
//    formData.append("isMonetized", isMonetized);

//    images.forEach((img) => {
//      if (img?.file) {
//        formData.append("images", img.file);
//      }
//    });

//    if (video?.file) {
//      formData.append("video", video.file);
//    }

//    const res = await api.post("/feeds/create", formData, {
//      headers: { "Content-Type": "multipart/form-data" },
//      onUploadProgress,
//    });

//    return res.data;
//  },

//  /* ===================== UPDATE POST ===================== */
//  updatePost: async ({ postId, description, images = [], video }) => {
//    const formData = new FormData();

//    if (description !== undefined) {
//      formData.append("description", description);
//    }

//    images.forEach((img) => {
//      if (img?.file) {
//        formData.append("images", img.file);
//      }
//    });

//    if (video?.file) {
//      formData.append("video", video.file);
//    }

//    const res = await api.patch(`/feeds/${postId}`, formData, {
//      headers: { "Content-Type": "multipart/form-data" },
//    });

//    return res.data; // { post }
//  },

//  /* ===================== DELETE POST ===================== */
//  deletePost: async ({ postId }) => {
//    const res = await api.delete(`/feeds/${postId}`);
//    return res.data;
//  },

//  /* ===================== LIKE / SAVE ===================== */
//  toggleLike: async ({ postId }) => {
//    const res = await api.post(`/likes/${postId}`);
//    return res.data;
//  },

//  /* ===================== POST BOOKMARK ===================== */
//  toggleSave: async ({ postId }) => {
//    const res = await api.post(`/saves/${postId}`);
//    return res.data;
//  },

//  /* ===================== FOLLOW ===================== */
//  followUser: async ({ userId }) => {
//    const res = await api.post(`/follows/${userId}/follow`);
//    return res.data;
//  },

//  handleRemoveProfile: async ({ type }) => {
//    await api.patch(`user/removeProfile`, {
//      type: type,
//    });
//  },

//  /* ===================== get state post (Cursor-based) ===================== */

//  getStatePost: async ({ cursor = null, limit = 10 }) => {
//    const params = { tab: "STATE", limit };
//    if (cursor) {
//      params.cursor = cursor;
//    }
//    const res = await api.get(`/feeds/prime`, { params });
//    return res.data;
//  },

//  /* ===================== get district post (Cursor-based) ===================== */

//  getDistrictPost: async ({ cursor = null, limit = 10 }) => {
//    const params = { tab: "DISTRICT", limit };
//    if (cursor) {
//      params.cursor = cursor;
//    }
//    const res = await api.get(`/feeds/prime`, { params });
//    return res.data;
//  },

//  /* ===================== get national post (Cursor-based) ===================== */

//  getNationalPost: async ({ cursor = null, limit = 10 }) => {
//    const params = { tab: "NATIONAL", limit };
//    if (cursor) {
//      params.cursor = cursor;
//    }
//    const res = await api.get(`/feeds/prime`, { params });
//    return res.data;
//  },

//  /* ===================== get All post ===================== */

//  getAllPost: async () => {
//    const res = await api.get(`/feeds/prime?tab=ALL`);
//    return res.data;
//  },

//  /* ===================== UNFOLLOW ===================== */

//  unfollowUser: async ({ userId }) => {
//    const res = await api.delete(`/follows/${userId}`);
//    return res.data;
//  },

//  /* ===================== FOLLOW REQUEST ===================== */
//  requestFollow: async ({ userId }) => {
//    const res = await api.post(`/follows/${userId}/follow`);
//    return res.data;
//  },

//  /* ===================== FOLLOW REQUEST CANCEL ===================== */
//  requestCancel: async ({ userId }) => {
//    const res = await api.delete(`/follows/request/${userId}/cancel`);
//    return res.data;
//  },

//  /* ===================== GET ALL COMMENTS OF POST ===================== */
//  getCommentsByPost: async ({ postId }) => {
//    const res = await api.get(`/comments/${postId}/comments`);
//    return res.data; // { comments }
//  },

//  /* ===================== ADD COMMENTS ===================== */
//  addComment: async ({ postId, content }) => {
//    const res = await api.post(`/comments/${postId}/comment`, { content });

//    return res.data; // { success, comment }
//  },

//  /* ===================== GET REPLIES OF A COMMENT ===================== */
//  getRepliesByComment: async ({ commentId }) => {
//    const res = await api.get(`/comments/comment/${commentId}/replies`);
//    return res.data; // { replies }
//  },

//  /* ===================== COMMENT REPLY ===================== */
//  addCommentReply: async ({ postId, content, parentCommentId }) => {
//    const res = await api.post(`/comments/${postId}/comment`, {
//      content,
//      parentCommentId, // optional (used for replies)
//    });
//    return res.data; // { success, comment }
//  },

//  /* ===================== COMMENT LIKE ===================== */
//  likeComment: async ({ commentId }) => {
//    const res = await api.post(`/comments/comment/${commentId}/like`);
//    return res.data;
//  },

//  /* ===================== REPORT POST ===================== */
//  reportPost: async ({ postId, reason }) => {
//    if (!postId || !reason) {
//      throw new Error("postId and reason are required");
//    }

//    const res = await api.post(`/feeds/${postId}/report`, { reason });

//    return res.data;
//    // { success, message }
//  },

//  /* ===================== GET SINGLE POST (Shared Post) ===================== */
//  getPostById: async ({ postId }) => {
//    if (!postId) {
//      throw new Error("postId is required");
//    }

//    const res = await api.get(`/feeds/${postId}`);

//    return res.data;
//  },

//  /* ===================== MONETIZED POST LIMIT  ===================== */
//  getMonetizedPostLimits: async ({ signal } = {}) => {
//    const res = await api.get(`/user/monetized-post-limits`, { signal });

//    return res.data;
//  },

//  /* ===================== GET MONETIZED POST COUNT FOR DAY  ===================== */
//  monetizedPostsCount: async () => {
//    const res = await api.get(`/feeds`);

//    return res.data;
//  },
// };
